import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@arcnet/contracts';
import { Icon } from '../../components/ui/Icon.js';
import { AuthLayout } from './AuthLayout.js';

/**
 * Forgot password (0176, 30 October 2026). Two signed-out pages:
 *   /forgot-password   an address and Cloudflare Turnstile; the answer is the same whether or not
 *                      the address has an account
 *   /reset-password#t  the emailed link (the token in the fragment, so no log holds it): a new
 *                      password, then back to sign in. Every other session has been ended by then.
 */

/** ArcNet's Turnstile widget, the same one as the website's contact form. Public by design. */
const TURNSTILE_SITE_KEY = '0x4AAAAAAFn5EsuBQi_nIBka';

interface TurnstileApi {
  render: (el: HTMLElement, o: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

let loading: Promise<TurnstileApi> | null = null;
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load')));
    s.onerror = () => { loading = null; reject(new Error('Turnstile did not load')); };
    document.head.appendChild(s);
  });
  return loading;
}

/** The check itself. `onToken` gets null when it expires or fails, so the button waits again. */
function Turnstile({ onToken, resetKey }: { onToken: (t: string | null) => void; resetKey: number }) {
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let id: string | null = null;
    let gone = false;
    loadTurnstile().then((t) => {
      if (gone || !box.current) return;
      id = t.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY, action: 'password-reset', theme: 'auto', size: 'flexible',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      });
    }, () => setFailed(true));
    return () => { gone = true; if (id && window.turnstile) window.turnstile.remove(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  return (
    <>
      <div ref={box} style={{ minHeight: 65, marginBottom: 12 }} />
      {failed && <div className="arc-hint" style={{ marginTop: -8, marginBottom: 12 }}>The check could not load. Reload the page to try again.</div>}
    </>
  );
}

function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
      <Icon name="warning" size={14} />
      <span>{error}</span>
    </div>
  );
}

async function post(path: string, body: unknown): Promise<void> {
  const res = await fetch(`/api/v1${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (res.ok) return;
  const b = await res.json().catch(() => ({})) as { detail?: string };
  throw new Error(res.status === 429 ? 'Too many tries. Wait a minute and try again.' : b.detail ?? 'Something went wrong. Try again.');
}

const backToSignIn = () => { window.location.assign('/'); };

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true); setError(null);
    try {
      await post('/auth/password-reset', { email: email.trim(), turnstile: token });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
      // A token works once: a new check for the next try.
      setToken(null); setResetKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout>
        <h1 className="arc-h1">Check your email</h1>
        <p className="arc-muted" style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.55 }}>
          If <strong>{email.trim()}</strong> has an ArcNet account, a link to choose a new password is on its way.
          It works once, for 30 minutes. Nothing arriving? Check your junk folder, or ask your company's ArcNet admin.
        </p>
        <button type="button" className="arc-btn" style={{ width: '100%', marginTop: 18 }} onClick={backToSignIn}>
          Back to sign in
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div style={{ marginBottom: 20 }}>
        <h1 className="arc-h1">Reset your password</h1>
        <p className="arc-muted" style={{ marginTop: 4, fontSize: 13 }}>We'll email you a link to choose a new one.</p>
      </div>
      <form onSubmit={submit} noValidate>
        <ErrorBox error={error} />
        <div className="arc-field">
          <label className="arc-label" htmlFor="reset-email">Email address</label>
          <input id="reset-email" type="email" className="arc-input" autoComplete="username" required autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Turnstile onToken={setToken} resetKey={resetKey} />
        <button type="submit" className="arc-btn arc-btn-primary" style={{ width: '100%' }}
          disabled={busy || !token || !/^\S+@\S+\.\S+$/.test(email.trim())}>
          {busy ? 'Sending…' : 'Email me a link'}
        </button>
        <button type="button" className="arc-btn arc-btn-ghost" style={{ width: '100%', marginTop: 8 }} onClick={backToSignIn}>
          Back to sign in
        </button>
      </form>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  // Read once and taken off the address bar, so it is not left in history or a screenshot.
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));
  useEffect(() => { window.history.replaceState({}, '', '/reset-password'); }, []);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const valid = /^[A-Za-z0-9_-]{43}$/.test(token);
  const canSubmit = password.length >= PASSWORD_MIN_LENGTH && password === confirm && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null);
    try {
      await post('/auth/password-reset/complete', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  };

  if (!valid || done) {
    return (
      <AuthLayout>
        <h1 className="arc-h1">{done ? 'Password changed' : 'This link is not valid'}</h1>
        <p className="arc-muted" style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.55 }}>
          {done
            ? 'Sign in with your new password. You have been signed out everywhere else.'
            : 'Open the link from the email again, or ask for a new one.'}
        </p>
        <button type="button" className="arc-btn arc-btn-primary" style={{ width: '100%', marginTop: 18 }}
          onClick={() => window.location.assign(done ? '/' : '/forgot-password')}>
          {done ? 'Sign in' : 'Ask for a new link'}
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div style={{ marginBottom: 20 }}>
        <h1 className="arc-h1">Choose a new password</h1>
        <p className="arc-muted" style={{ marginTop: 4, fontSize: 13 }}>At least {PASSWORD_MIN_LENGTH} characters.</p>
      </div>
      <form onSubmit={submit} noValidate>
        <ErrorBox error={error} />
        <div className="arc-field">
          <label className="arc-label" htmlFor="new-password">New password</label>
          <input id="new-password" type="password" className="arc-input" autoComplete="new-password" required autoFocus
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="arc-field">
          <label className="arc-label" htmlFor="confirm-password">Type it again</label>
          <input id="confirm-password" type="password" className="arc-input" autoComplete="new-password" required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={confirm.length > 0 && confirm !== password} />
          {confirm.length > 0 && confirm !== password && <div className="arc-hint">The two do not match yet.</div>}
        </div>
        <button type="submit" className="arc-btn arc-btn-primary" style={{ width: '100%', marginTop: 4 }} disabled={!canSubmit}>
          {busy ? 'Saving…' : 'Set the new password'}
        </button>
      </form>
    </AuthLayout>
  );
}
